import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { toast } from 'react-toastify';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import { sortAdminItems } from '../common/utils/tableUtil';
import SearchBar from '../common/search/SearchBar';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import SlaAlertsModal from './modals/slaAlerts/slaAlertsModal';
import SlaAlertsDeleteModal from './modals/slaAlerts/slaAlertsDeleteModal';

const AlertsTable = ({ items, onToggle, sortConfig, onSortChange, setAlerts, fetchAlerts }) => {

    const handleUpdate = (slaAlert) =>  {
      toast.success(`${slaAlert.name} Updated`, { autoClose: 3000 });
      fetchAlerts();
    }
    
    const handleDelete = (slaAlert) =>  {
      setAlerts(items.filter(items => items.id !== slaAlert.id));
      toast.success(`${slaAlert.name} Deleted`, { autoClose: 3000 });
    }
    
    const handleSort = (key) => {
        const direction =
        sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        onSortChange({ key, direction });
    }

    const SortIcon = ({ field }) => {
        if (sortConfig.key !== field) return null;
        return sortConfig.direction === 'asc' ? (
        <ArrowDropUp fontSize="small" />
        ) : (
        <ArrowDropDown fontSize="small" />
        );
    }

    return (
        <>
          <TableContainer component={Paper} sx={{ mt: 4 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">    
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0568AE' }}>
                  {['Alert Name', 'Description', 'Active'].map((field) => (
                    <TableCell
                      key={field}
                      onClick={() => handleSort(field)}
                      sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                        <SortIcon field={field} />
                      </Stack>
                    </TableCell>                
                  ))}
                  <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                    <TableCell sx={{ color: '#FFFFFF' }}>{item.name}</TableCell>
                    <TableCell sx={{ color: '#FFFFFF' }}>{item.description}</TableCell>              
                    <TableCell> <Switch checked={!!item.active} onChange={(e) => onToggle(item, 'active', e.target.checked)} /> </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <SlaAlertsModal slaAlert={ item } onTriggerUpdate={handleUpdate} />
                        <SlaAlertsDeleteModal slaAlert={ item } onTriggerDelete={handleDelete} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      );
}

export default function SlaAlerts() {

    const [alerts, setAlerts] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchAlerts();
    }, []);
    
    const fetchAlerts = async () => {
        try {            
            const response = await fetch(`${process.env.REACT_APP_API}/slaAlerts/`);
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setAlerts(json.data);
            }
        } catch (error) {
            toast.error(error);
        }
    }

    const handleCreate = (slaAlert) =>  {
      toast.success(`${slaAlert.name} Created `, { autoClose: 3000 });
      fetchAlerts();
    }

    const handleUpdate = async (alert) => {
        const res = await fetch(`${process.env.REACT_APP_API}/slaAlerts/${alert.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(alert)
        });
        if (res.ok) {
            setAlerts((prev) => prev.map((p) => (p.id === alert.id ? alert : p)));
            if (alert.active) {
                toast.success(`${alert.name} Activated`);
            } else {
                toast.success(`${alert.name} Deactivated`);
            }
        } else {
          toast.error(`Error: ${res.status}`);
        }
    };

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    };

    const filtered = alerts.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['name', 'description'].some((field) => item[field]?.toLowerCase().includes(s));
    });

    const sorted = sortAdminItems(filtered, sortConfig);
    const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


    return (
        <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
            <Box sx={{pt: 2}}>
                <ClassificationBannerHeader />
                <PageHeader title="Sla Alerts" />
                <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                      <SearchBar
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search Alerts..."
                          label="Search Alerts..."
                          width={200}
                      />                
                </Stack>
              
                <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                    <AlertsTable
                        items={paginated}
                        onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                        sortConfig={sortConfig}
                        onSortChange={handleSortChange}
                        setAlerts={setAlerts}
                        fetchAlerts={fetchAlerts}
                    />
                    <TablePagination
                        component="div"
                        count={sorted.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                    />
                </TableContainer>

                <Stack direction="row" mt={2} mb={1}>
                  <SlaAlertsModal slaAlert='' onTriggerCreate={handleCreate} />
                </Stack>

            </Box>
        </Container>
    );
}